import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RegistroDto, UpdateRegistroDto } from "@/api";
import { isNil, keyBy } from "lodash";

export type Registri_GFL_DFA_3500 = {
  SPaltaP?: number;
  "SP-t-altaP"?: number;
  "SP-1Ph"?: number;
  "SP-2Ph"?: number;
  "SP-1Cr"?: number;
  "SP-2Cr"?: number;
  email1?: string;
  email2?: string;
  funzman?: boolean;
};

export function registriToForm_GFL_DFA_3500(
  _registri: RegistroDto[]
): Partial<Registri_GFL_DFA_3500> {
  const registri = keyBy(_registri, "registro");

  return {
    SPaltaP: isNil(registri["SPaltaP"])
      ? undefined
      : parseFloat(registri["SPaltaP"].value),
    "SP-t-altaP": isNil(registri["SP-t-altaP"])
      ? undefined
      : parseFloat(registri["SP-t-altaP"].value),
    "SP-1Ph": isNil(registri["SP-1Ph"])
      ? undefined
      : parseFloat(registri["SP-1Ph"].value),
    "SP-2Ph": isNil(registri["SP-2Ph"])
      ? undefined
      : parseFloat(registri["SP-2Ph"].value),
    "SP-1Cr": isNil(registri["SP-1Cr"])
      ? undefined
      : parseFloat(registri["SP-1Cr"].value),
    "SP-2Cr": isNil(registri["SP-2Cr"])
      ? undefined
      : parseFloat(registri["SP-2Cr"].value),
    email1: registri["email1"]?.value || "",
    email2: registri["email2"]?.value || "",
    funzman:
      registri["funzman"]?.value === "1" ||
      registri["funzman"]?.value === "true",
  };
}

export function formToRegistri_GFL_DFA_3500(
  form: Registri_GFL_DFA_3500
): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_GFL_DFA_3500]?: UpdateRegistroDto;
  } = {
    SPaltaP: isNil(form.SPaltaP)
      ? undefined
      : {
          registro: "SPaltaP",
          topicPrefix,
          value: form.SPaltaP,
        },
    "SP-t-altaP": isNil(form["SP-t-altaP"])
      ? undefined
      : {
          registro: "SP-t-altaP",
          topicPrefix,
          value: form["SP-t-altaP"],
        },
    "SP-1Ph": isNil(form["SP-1Ph"])
      ? undefined
      : {
          registro: "SP-1Ph",
          topicPrefix,
          value: form["SP-1Ph"],
        },
    "SP-2Ph": isNil(form["SP-2Ph"])
      ? undefined
      : {
          registro: "SP-2Ph",
          topicPrefix,
          value: form["SP-2Ph"],
        },
    "SP-1Cr": isNil(form["SP-1Cr"])
      ? undefined
      : {
          registro: "SP-1Cr",
          topicPrefix,
          value: form["SP-1Cr"],
        },
    "SP-2Cr": isNil(form["SP-2Cr"])
      ? undefined
      : {
          registro: "SP-2Cr",
          topicPrefix,
          value: form["SP-2Cr"],
        },
    email1: isNil(form.email1)
      ? undefined
      : {
          registro: "email1",
          topicPrefix,
          value: mapEmptyStringToSlash(form.email1),
        },
    email2: isNil(form.email2)
      ? undefined
      : {
          registro: "email2",
          topicPrefix,
          value: mapEmptyStringToSlash(form.email2),
        },
    funzman: isNil(form.funzman)
      ? undefined
      : {
          registro: "funzman",
          topicPrefix,
          value: form.funzman ? "1" : "0",
        },
  };
  return Object.values(_values);
}

function mapEmptyStringToSlash(value: string) {
  if (value === "") {
    return "/";
  }
  return value;
}

export function EditorRegistri_GFL_DFA_3500({
  control,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_GFL_DFA_3500>;
  errors: FieldErrors<Registri_GFL_DFA_3500>;
  register: UseFormRegister<Registri_GFL_DFA_3500>;
  getValues: UseFormGetValues<Registri_GFL_DFA_3500>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Set Point Pressione */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflDfa3500.sezioni.setPointPressione")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.spAltaPressione")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SPaltaP"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.bar")}
                            </InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.spTAltaPressione")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SP-t-altaP"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 120 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.secondiAbbreviazione")}
                            </InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 0 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Soglie pH e Cr */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflDfa3500.sezioni.sogliePhCr")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.soglia1Ph")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SP-1Ph"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.soglia2Ph")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SP-2Ph"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.soglia1Cr")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SP-1Cr"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.ppm")}
                            </InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.soglia2Cr")}
                  </Typography>
                  <Controller
                    control={control}
                    name="SP-2Cr"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">
                              {t("unita.ppm")}
                            </InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 2 }}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      {/* Email */}
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflDfa3500.sezioni.configurazioneEmailModalita")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 100 }}
                  >
                    {t("gflDfa3500.campi.email1")}
                  </Typography>
                  <TextField
                    {...register("email1")}
                    error={!!errors.email1?.message}
                    helperText={errors.email1?.message || null}
                    fullWidth
                    disabled={readOnly}
                  />
                </Box>
              </Grid>

              <Grid item sm={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                    sx={{ minWidth: 100 }}
                  >
                    {t("gflDfa3500.campi.email2")}
                  </Typography>
                  <TextField
                    {...register("email2")}
                    error={!!errors.email2?.message}
                    helperText={errors.email2?.message || null}
                    fullWidth
                    disabled={readOnly}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflDfa3500.sezioni.modalita")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflDfa3500.campi.funzionamentoManuale")}
                  </Typography>
                  <Controller
                    control={control}
                    name="funzman"
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <Switch
                          checked={field.value}
                          color="success"
                          {...field}
                          disabled={readOnly}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
